<template>
  <PageView
    :other-applications-number="otherApplications?.length"
    :own-applications-number="ownApplications?.length"
    @update-applications-list="updateListApplications($event)"
  >
    <h1
      class="title main-title"
      :aria-label="showOwnApplications ? $t('titles.applications-page-other') : $t('titles.applications-page')"
    >
      {{
        showOwnApplications ? $t("titles.applications-page-other") : $t("titles.applications-page")
      }}
    </h1>
    <div class="columns section_filter_order_create">
      <div :class="canCreateApplication ? 'column is-offset-6 is-2' : 'column is-offset-8 is-2'">
        <b-field :label="$t('applications.name')">
          <b-autocomplete
            v-model="filterName"
            :aria-label="$t('applications.name')"
            :data="selectedApplications"
            field="localName"
            rounded
            expanded
          />
        </b-field>
      </div>
      <div class="column is-2">
        <b-field :label="$t('applications.trier')">
          <b-select
            v-model="alphabeticalOrDateSort"
            :aria-label="$t('applications.trier')"
            rounded
            expanded
            @update:model-value="orderByDateOrName(applications || [], $event)"
          >
            <option
              value="0_9"
              :aria-label="$t('applications.trierRecent')"
            >
              {{ $t("applications.trierRecent") }}
            </option>
            <option
              value="9_0"
              :aria-label="$t('applications.trierAncien')"
            >
              {{ $t("applications.trierAncien") }}
            </option>
            <option
              value="Az"
              :aria-label="$t('applications.trierA_z')"
            >
              {{ $t("applications.trierA_z") }}
            </option>
            <option
              value="Za"
              :aria-label="$t('applications.trierZ_a')"
            >
              {{ $t("applications.trierZ_a") }}
            </option>
          </b-select>
        </b-field>
      </div>
      <div
        v-if="canCreateApplication"
        class="column is-2"
      >
        <b-button
          :aria-label="$t('applications.create')"
          icon-left="plus"
          size="is-large"
          type="is-dark"
          class="btnCreate"
          tabindex="0"
          expanded
          @keyup.enter="createApplication((applications || []).map((a) => a.application.name))"
          @keyup.space="createApplication((applications || []).map((a) => a.application.name))"
          @click="createApplication((applications || []).map((a) => a.application.name))"
        />
      </div>
    </div>
    <div
      v-if="progress && progress < 1"
      class="columns"
    >
      <div class="column">
        <b-progress
          :value="progress * 100"
          show-value
          size="is-medium"
          type="is-primary"
        >
          <span v-if="progress === 0">
            <LoadingAnimate :size="'is-small'" />
          </span>
          <span v-else> {{ Math.floor(progress * 100) }}% </span>
        </b-progress>
      </div>
    </div>
    <div
      v-else
      class="columns applicationsList"
    >
      <ApplicationCard
        v-for="(selectedApplication, index) in selectedApplications"
        :key="selectedApplication.application.name"
        class="ApplicationCard column is-2-widescreen is-6-desktop is-12-tablet"
        :application-roles="
          selectedApplication?.currentApplicationUserRolesResult?.applicationRoles || []
        "
        :application-light-result="selectedApplication"
        :current="current"
        :index="index"
        :per-page="perPage"
      />
    </div>
    <hr>
    <b-pagination
      v-if="perPage <= selectedApplications.length"
      v-model="current"
      v-model:current-page="current"
      :aria-current-label="$t('menu.aria-curent-page')"
      :aria-label="$t('menu.aria-pagination')"
      :aria-next-label="$t('menu.aria-next-page')"
      :aria-previous-label="$t('menu.aria-previous-page')"
      :per-page="perPage"
      :range-after="2"
      :range-before="2"
      :rounded="true"
      :total="selectedApplications.length"
      order="is-centered"
      role="navigation"
      @change="changePage"
    />
  </PageView>
</template>

<script lang="ts">
import PageView from "@/views/common/PageView.vue";
import LoadingAnimate from "@/components/common/LoadingAnimate.vue";
import services from "@/composable/services";
import useBoolean from "@/composable/components/boolean";
import useText from "@/composable/components/text";
import { computed, onMounted, inject, ref, ShallowRef, Ref } from "vue";
import { useRedirections } from "@/composable/applications/useFunction";
import ApplicationCard from "@/components/application/ApplicationCard.vue";
import useNumber from "@/composable/components/number";
import "@/style/views/application/ApplicationsView.scss";
import { BAutocomplete, BButton, BField, BPagination, BProgress, BSelect } from "buefy";
import { LocalApplicationLightResult } from "@/types/front/LocalApplicationLightResult";

export default {
  name: "ApplicationsViewV2",
  components: {
    BPagination,
    BProgress, BAutocomplete, BField, BSelect, BButton, ApplicationCard, LoadingAnimate, PageView },
  setup() {
    const { createApplication, updateApplication, displayAdditionalFilesManagement }
      = useRedirections();
    const applications: ShallowRef<LocalApplicationLightResult[]> | undefined = inject("application:applications");
    const ownApplications: ShallowRef<LocalApplicationLightResult[]> | undefined = inject("application:ownApplications");
    const showOwnApplications: Ref<number, number> | undefined = inject("application:showOwnApplications");
    const hasOwnApplications: Ref<boolean, boolean> | undefined = inject("application:hasOwnApplications");
    const otherApplications: ShallowRef<LocalApplicationLightResult[]> | undefined = inject("application:otherApplications");
    const progress: Ref<number, number> | undefined = inject("application:progress");
    const loadApplications: ((filter: any) => Promise<void>) | undefined = inject("application:loadApplications");
    const checkboxDate = useBoolean().refBoolean;
    const currentUser = computed(() => services.loginService.getAuthenticatedUser());
    const canManageCreateApplication
      = currentUser.value.authorizedForApplicationCreation || currentUser.value.openAdomAdmin;
    const canCreateApplication
      = currentUser.value.authorizedForApplicationCreation && currentUser.value.authorizations.length;
    const loading = computed(() => !progress || progress.value < 1);
    const isCardModalActive = useBoolean().refBoolean;
    // show modal and cards
    const selectedName = useText().refText;
    // pagination variable
    const { refNumber: current } = useNumber(1);
    const perPage = 12;
    const offset = useNumber(0);
    // filtre variable
    const filterName = useText().refText;
    const selected = useBoolean().refBoolean;
    const alphabeticalOrDateSort = useText().refText;
    const alphabeticalOrDateSortRefreshKey = ref(0);
    const selectedApplications = computed(() => {
      // Forced call to alphabeticalOrDateSortRefreshKey to allow recomputation
      // as ownApplications and otherApplications are not dynamic refs
      void alphabeticalOrDateSortRefreshKey.value;
      let applicationsListOrderOrFilter
        = showOwnApplications?.value === 0 ? ownApplications?.value : otherApplications?.value;
      if (filterName.value.length > 2 && applicationsListOrderOrFilter) {
        return applicationsListOrderOrFilter.filter(
          a => a.application.localName.toString().toLowerCase().indexOf(filterName.value.toLowerCase()) >= 0,
        );
      }
      return applicationsListOrderOrFilter || [];
    });

    async function changePage(page: number) {
      offset.refNumber.value = (page - 1) * perPage;
    }

    onMounted(async () => {
      if (loadApplications) {
        loadApplications(["DATATYPE", "REFERENCETYPE", "CONFIGURATION", "ADDITIONALFILE"]);
      }
      alphabeticalOrDateSort.value = "9_0";
    });

    function updateListApplications(event: string) {
      if (!showOwnApplications) {
        return;
      }
      if (event === "ownApplications") {
        showOwnApplications.value = 0;
      }
      else if (event === "otherApplications") {
        showOwnApplications.value = 1;
      }
    }

    function orderByDateOrName(localApplications: LocalApplicationLightResult[], event: string) {
      if (event === "0_9" || event === "9_0") {
        sortedApplicationsListForDate(localApplications, event);
      }
      else {
        sortedApplicationsListForName(localApplications, event);
      }
    }

    function sortedApplicationsListForDate(localApplications: LocalApplicationLightResult[], event: string) {
      if (!showOwnApplications || !ownApplications || !otherApplications) {
        return;
      }
      localApplications
        = showOwnApplications.value === 0 ? ownApplications.value : otherApplications.value;
      localApplications.sort((a, b) => {
        // TODO Typing : Vérifier qu’on a bien des dates dans updateDate
        // const localA = new Date(
        //  a.application.updateDate[0],
        //  a.application.updateDate[1] - 1,
        //  a.application.updateDate[2],
        //  a.application.updateDate[3],
        //  a.application.updateDate[4],
        //  a.application.updateDate[5],
        //  a.application.updateDate[6] / 1000000,
        // );
        // const localB = new Date(
        //  b.application.updateDate[0],
        //  b.application.updateDate[1] - 1,
        //  b.application.updateDate[2],
        //  b.application.updateDate[3],
        //  b.application.updateDate[4],
        //  b.application.updateDate[5],
        //  b.application.updateDate[6] / 1000000,
        // );
        return event === "0_9"
          ? b.application.updateDate.getTime() - a.application.updateDate.getTime()
          : a.application.updateDate.getTime() - b.application.updateDate.getTime();
      });
      if (showOwnApplications.value === 0) {
        ownApplications.value = localApplications;
      }
      else {
        otherApplications.value = localApplications;
      }
      alphabeticalOrDateSortRefreshKey.value = Date.now();
    }

    function sortedApplicationsListForName(localApplications: LocalApplicationLightResult[], event: string) {
      if (!showOwnApplications || !ownApplications || !otherApplications) {
        return;
      }
      localApplications
        = showOwnApplications.value === 0 ? ownApplications?.value : otherApplications?.value;
      if (event === "Az") {
        localApplications = localApplications.sort((a, b) =>
          a.application.localName.localeCompare(b.application.localName),
        );
      }
      else if (event === "Za") {
        localApplications = localApplications
          .sort((a, b) => a.application.localName.localeCompare(b.application.localName))
          .reverse();
      }
      if (showOwnApplications.value === 0) {
        ownApplications.value = localApplications;
      }
      else {
        otherApplications.value = localApplications;
      }
      alphabeticalOrDateSortRefreshKey.value = Date.now();
    }

    return {
      ownApplications,
      hasOwnApplications,
      otherApplications,
      showOwnApplications,
      applications,
      progress,
      loadApplications,
      createApplication,
      updateApplication,
      canManageCreateApplication,
      displayAdditionalFilesManagement,
      current,
      perPage,
      filterName,
      selected,
      selectedApplications,
      currentUser,
      loading,
      selectedName,
      checkboxDate,
      alphabeticalOrDateSort,
      isCardModalActive,
      canCreateApplication,
      orderByDateOrName,
      updateListApplications,
      changePage,
    };
  },
};
</script>
